import { customElement } from 'lit/decorators.js';
import { EventGetReference, EventSnippet, EventTemplate } from '../../../webmodule-common/other/ui/events';
import {
  FabricationJobState,
  FabricationJobStateCount,
  FabricationJobSummaryView
} from '../../api/fabrication-job-api';
import { information } from '../../../webmodule-common/other/ui/modal-option';
import {
  isOptimisticLockOwner,
  optimisticLock,
  optimisticLockRelease
} from '../../dealer-franchisee/common/optimistic-lock';
import { FabricationJobSummaryTable } from './fabrication-job-summary-table';
import { getApiFactory } from '../../api/api-injector';
import { html } from 'lit';
import { LangFabricationJobListView } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-list-view-lang';
import { IconRefresh } from '../../../webmodule-common/other/ui/icons/icon-refresh';
import { newGuid } from '../../../webmodule-common/other/api/guid';
import { PageControl, PageControlOptions, PageManager } from '../../../webmodule-common/other/ui/page-control';
import { ViewBase } from '../../../webmodule-common/other/ui/view-base';

export interface FabricationJobListViewOptions {
  branchId: EventGetReference;
}

@customElement('wm-fabricationjoblistview')
export class FabricationJobListView extends ViewBase {
  protected readonly branchId: EventGetReference;
  protected pageControl: PageControl;
  private readonly elementId: string;
  protected tables: FabricationJobSummaryTable[];
  protected stateCounts: FabricationJobStateCount[] = [];
  protected staleJobs: string[] = [];

  constructor(options: FabricationJobListViewOptions) {
    super();
    this.elementId = `fabrication-job-list-view-${newGuid()}`;
    this.branchId = options.branchId;
    this.tables = this.initializeTables();
    this.pageControl = this.createPageControl();
  }

  public async setActiveTabByHash() {
    await this.pageControl?.applyWindowHash();
  }

  public async afterConstruction(): Promise<void> {
    await this.updatePageTotals();
    const table = this.pageControl.activePage?.data as FabricationJobSummaryTable;
    if (table) await table.refreshData(1);
  }

  protected createPageControl(): PageControl {
    const getInitialPageManagers = (): PageManager[] => {
      return this.tables.map(table => {
        const pm: PageManager = {
          caption: table.eventTitle,
          canClose: () => Promise.resolve(false),
          canLeave: () => Promise.resolve(true),
          hasDelete: () => false,
          onEnter: async () => {
            table.delayedDataLoad = false;
            if (table.dataIsStale) table.refreshData();
          },
          content: () => table,
          data: table,
          pageFragment: table.pageFragment
        };
        return pm;
      });
    };

    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: [
        {
          event: async () => {
            const table = this.pageControl.activePage?.data as FabricationJobSummaryTable;
            if (table) {
              this.beforeRefresh();
              await table.refreshData();
              this.tables.forEach(t => {
                if (t !== table) t.dataIsStale = true;
              });
              this.afterRefresh();
              return true;
            }
            return false;
          },
          caption: () => html`${new IconRefresh()}`
        }
      ],
      plusPage: {
        caption: () => LangFabricationJobListView.createFabricationJob(),
        event: async () => await this.addNewFabricationJob()
      },
      pageInitializer: () => getInitialPageManagers()
    };
    return new PageControl(options);
  }

  beforeRefresh() {
    this.staleJobs = [];
  }

  afterRefresh() {
    // overridden in subclass
  }

  protected async updatePageTotals() {
    const api = getApiFactory().fabricationJob();
    const result = await api.getFabricationJobTotalsByState({ branchId: await this.branchId() });
    if (result) this.stateCounts = result.stateCounts;
    this.pageControl.requestUpdate();
  }

  protected getCount(...states: FabricationJobState[]): number {
    return this.stateCounts.filter(sc => states.includes(sc.state)).reduce((sum, sc) => sum + sc.count, 0);
  }

  protected initializeTables(): FabricationJobSummaryTable[] {
    const makeTable = (titleStr: () => string, states: FabricationJobState[], pageFragment: string) => {
      const title: EventSnippet = () => {
        const count = this.getCount(...states);
        return html`${titleStr()} (${count})`;
      };
      return new FabricationJobSummaryTable({
        states,
        title,
        branchId: this.branchId,
        pageFragment,
        deleteJobEvent: async job => await this.deleteJob(job),
        stale: { exists: (id: string) => this.staleJobs.includes(id) }
      });
    };

    return [
      makeTable(
        () => LangFabricationJobListView.allCurrentTabTitle(),
        [
          FabricationJobState.Draft,
          FabricationJobState.Planned,
          FabricationJobState.AwaitingOrder,
          FabricationJobState.ReadyForProduction,
          FabricationJobState.InProduction
        ],
        'allCurrent'
      ),
      makeTable(() => LangFabricationJobListView.draftTabTitle(), [FabricationJobState.Draft], 'draft'),
      makeTable(() => LangFabricationJobListView.plannedTabTitle(), [FabricationJobState.Planned], 'planned'),
      makeTable(
        () => LangFabricationJobListView.awaitingOrderTabTitle(),
        [FabricationJobState.AwaitingOrder],
        'awaitingOrder'
      ),
      makeTable(
        () => LangFabricationJobListView.readyForProductionTabTitle(),
        [FabricationJobState.ReadyForProduction],
        'readyForProduction'
      ),
      makeTable(
        () => LangFabricationJobListView.inProductionTabTitle(),
        [FabricationJobState.InProduction],
        'inProduction'
      ),
      makeTable(() => LangFabricationJobListView.completedTabTitle(), [FabricationJobState.Completed], 'completed')
    ];
  }

  protected async deleteJob(job: FabricationJobSummaryView): Promise<void> {
    // Acquire a lock to ensure no other user is currently editing this job.
    // NOTE: The same lock-before-delete pattern may be missing from quote list deletion.
    const lock = await optimisticLock('fabricationjob', job.id);
    if (!isOptimisticLockOwner(lock)) {
      await information(
        LangFabricationJobListView.cannotDeleteMessage(),
        LangFabricationJobListView.cannotDeleteTitle()
      );
      return;
    }

    try {
      const api = getApiFactory().fabricationJob();
      // Fetch the latest record version — the summary list data can be stale
      const latest = await api.getFabricationJob({ id: job.id });
      if (!latest) return;
      const result = await api.deleteFabricationJob({
        id: job.id,
        recordVersion: latest.fabricationJob.recordVersion
      });
      if (result) {
        await this.updatePageTotals();
        const table = this.pageControl.activePage?.data as FabricationJobSummaryTable;
        if (table) await table.refreshData();
      }
    } finally {
      await optimisticLockRelease(job.id);
    }
  }

  async addNewFabricationJob(): Promise<boolean> {
    const branchId = await this.branchId();
    const id = newGuid();
    const result = await getApiFactory().fabricationJob().createFabricationJob({
      id,
      branchId,
      title: '',
      description: ''
    });
    if (result) {
      window.location.hash = '';
      window.location.pathname = `/fabrication-jobs/${result.fabricationJob.id}`;
      return true;
    }
    return false;
  }

  protected template(): EventTemplate {
    return html`<div id=${this.elementId} class="page-content">${this.pageControl?.ui}</div>`;
  }
}
